<script>
import StoresServices from '@/services/stores'
import EnvironmentUtils from '@/utils/environmentUtils'
import VueUtils from '@/utils/vueUtils'
import DialogUtils from '@/utils/dialogUtils'
import FloatingLabelTextInput from '@/components/FloatingLabelTextInput'
import VueEditor from '@/components/vue2-editor/VueEditor'
import Button from '@/components/Button'
import FileUploadZone from '@/components/FileUploadZone'
import FieldsValidator from '@/components/FieldsValidator'
import SelectHours from '@/components/SelectHours'

export default
{
    name: 'store-editor',

    created ()
    {
        const storeId = this.$route.params.id
        if (storeId)
        {
            StoresServices.fetchStores(EnvironmentUtils.cityId).then(async stores  =>
            {
                await this.$store.commit('stores/updateStoreList', stores)
                this.store = VueUtils.getObserverless(this.$store.getters.stores.getStoreById(storeId))
                if (!this.store) return this.$toast.warn('Le commerce que vous cherchez n\'existe pas')

                for (let pictureUrl of this.store.pictures)
                {
                    this.$refs.fileUploadZone.addToUploadedFiles(pictureUrl)
                }

                for (let [dayIndex, day] of this.store.openHours.entries())
                {
                    if (day) this.$refs.selectHours.setDay(dayIndex, day)
                }
            })

            StoresServices.fetchStoreLikeCount(EnvironmentUtils.cityId, storeId).then(count => this.likeCount = count)

            this.store = VueUtils.getObserverless(this.$store.getters.stores.getStoreById(storeId)) || {}
        }
    },

    render () 
    {
        return (
            <div id='store-editor-view'>
                <div id='header-actions'>
                    <Button id='back-button' type='secondary' outlined={true} text='Annuler' icon={require('@/assets/img/left-arrow.svg')} on-click={this.onBackButtonClicked} />
                    <Button id='delete-button' type='danger' outlined={true} text='Supprimer' on-click={this.onDeleteButtonClicked} v-show={this.storeExists} />
                </div>

                <div class='like-count'>
                    <inline-svg class='like-icon' src={require('@/assets/img/heart.svg')} />
                    <span class='description'>
                        <b class='count-value'>{this.likeCount} </b> personne{this.likeCount > 1 ? 's' : ''} aime{this.likeCount > 1 ? 'nt' : ''} votre commerce
                    </span>
                </div>

                <fields-validator ref='fieldsValidator'>
                    <floating-label-text-input id='store-name' placeholder='Nom du commerce' v-model={this.store.name} />
                    <vue-editor id='store-description' placeholder='Description du commerce' v-model={this.store.description} />
                    <file-upload-zone validation={1} ref='fileUploadZone' label='Ajouter des photos du commerce' accept='.png, .jpeg, .jpg, .svg' max-file-count={15} />
                    <floating-label-text-input optional={true} id='store-phone' placeholder='Numéro de téléphone' type='tel' v-model={this.store.phone} />
                    <floating-label-text-input optional={true} id='store-email' placeholder='Courriel' type='email' v-model={this.store.email} />
                    <floating-label-text-input optional={true} id='store-address' placeholder='Adresse du commerce' v-model={this.store.address} />
                    <floating-label-text-input optional={true} id='store-website' placeholder='Site web' type='url' v-model={this.store.website} />
                </fields-validator>

                <select-hours id='store-open-hours' ref='selectHours' />
                
                <Button id='update-button' type='primary' text='Mettre à jour' has-shadow={true}
                    v-show={this.storeExists} on-click={this.onUpdateButtonClicked} />
                <Button id='add-button' type='primary' text='Ajouter ce commerce' has-shadow={true}
                    v-show={!this.storeExists} on-click={this.onAddButtonClicked} />
            </div>
        )
    },

    data () 
    {
        return {
            store: {},
            likeCount: 0
        }
    },

    computed:
    {
        storeExists ()
        {
            return this.store && this.store.id
        },
    },

    mounted ()
    {
    },

    methods:
    {
        onBackButtonClicked ()
        {
            this.$router.back()
        },

        async onDeleteButtonClicked (e, button)
        {
            await button.showSpinnerWhile(async () =>
            {
                const confirm = await DialogUtils.waitForUserConfirmationAsync(
                {
                    description: 'Voulez-vous vraiment supprimer ce commerce ? Vous ne pourrez plus le récuperer par la suite.'
                })
                if (!confirm) return

                console.log('Deleting store', this.store.id)
                const res = await StoresServices.deleteStore(this.store.id, EnvironmentUtils.cityId, this.$store.state.user.token)
                console.log('Store deleted', res)

                await this.$store.dispatch('stores/removeStoreById', this.store.id)
                this.$router.push('/edit/stores')
            })
        },

        async onUpdateButtonClicked (e, button)
        {
            await button.showSpinnerWhile(async () =>
            {
                const store = this.createStoreObjectFromContext()
                if (!store) return

                console.log('Updating store', this.store, 'with', store)
                const res = await StoresServices.updateStore(store, EnvironmentUtils.cityId, this.$store.state.user.token)
                console.log('Store updated', res)

                await this.$store.commit('stores/updateStoreList', res)
                this.$router.push('/stores')
            })  
        },

        async onAddButtonClicked (e, button)
        {
            await button.showSpinnerWhile(async () =>
            {
                const store = this.createStoreObjectFromContext()
                if (!store) return

                console.log('Creating store', store, 'from', this.store)
                const res = await StoresServices.createStore(store, EnvironmentUtils.cityId, this.$store.state.user.token)
                console.log('Store created', res)

                await this.$store.commit('stores/updateStoreList', res)
                this.$router.push('/stores')
            })
        },

        createStoreObjectFromContext ()
        {
            if (!this.$refs.fieldsValidator.validate() || !this.$refs.selectHours.validate())
            {
                return false
            }

            const openHours = new Array(7)
            for (let [dayIdx, day] of Object.entries(this.$refs.selectHours.days))
            {
                openHours[dayIdx] = day
            }

            const store =
            {
                id: this.store.id,
                name: this.store.name,
                description: this.store.description,
                phone: this.store.phone,
                address: this.store.address,
                website: this.store.website,
                email: this.store.email,
                pictures: Object.values(this.$refs.fileUploadZone.filesUploaded),
                openHours
            }

            return store
        }
    },

    components:
    {
        FloatingLabelTextInput,
        VueEditor,
        Button,
        FileUploadZone,
        FieldsValidator,
        SelectHours
    }
}
</script>

<style lang='less'>
@import '~@/styles/main.less';

#store-editor-view
{
    margin-top: 12vw;
    margin-bottom: 4vw;
    margin-left: 2.5vw;
    margin-right: 2.5vw;

    .OnDesktop({
        border-left: var(--theme-vertical-menu-total-vertical-space) solid transparent;
        margin-top: 4vw;
        padding-left: 10vw;
        padding-right: 10vw;
    });

    #header-actions
    {
        display: flex;
        flex-direction: row;

        #back-button
        {
            background-color: var(--theme-border-color-extra-light);
            border-color: transparent;
            width: 30vw;

            .OnDesktop({
                width: 8vw;
                padding: 1vh 1vw;
            });
        }

        #delete-button
        {
            width: auto;
            margin-left: auto;
            background-color: var(--theme-border-color-extra-light);
            border-color: transparent;
        }
    }

    .like-count
    {
        display: flex;
        flex-direction: row;
        align-items: center;
        margin-top: 20px;

        .like-icon
        {
            fill: var(--theme-love-color);
            .WidthHeight(20px);
            margin-right: 10px;
        }

        .description
        {
            color: var(--theme-text-color-extra-light);


            .count-value
            {
                font-weight: bold;
                color: inherit;
            }
        }
    }

    .floating-label-text-input
    {
        margin-top: 15px;
    }

    #store-name
    {
        margin-top: 30px;
    }

    #store-description
    {
        margin-top: 20px;
    }

    #store-pictures-upload, .file-upload-zone
    {
        margin-top: 20px;
    }

    #store-open-hours
    {
        width: -webkit-fill-available;
        margin-top: 30px;   
    }

    #add-button, #update-button
    {
        margin-top: 20px;
        width: 100%;
    }
}
</style>



// WEBPACK FOOTER //
// src/views/StoreEditor.vue